/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
// v77.72 — Public Pricing landing page.
//
// One plan, no tiers — €10.99/month with a 3-month minimum commitment.
// The page mirrors the Upgrade-flow pricing copy so the public marketing
// page and the in-app upgrade card never drift. FAQ uses the same
// language as the Terms § 9 (Subscription, billing & cancellation) so
// users don't see a different story across pages.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import { trackEvent } from "@/lib/analytics";
import {
  MONTHLY_PRICE_EUR,
  YEARLY_PRICE_EUR,
  MONTHLY_MIN_TOTAL,
  YEARLY_MONTHLY_EQUIV,
  YEARLY_SAVINGS_EUR,
} from "@/lib/pricing";
import {
  Check,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  ShieldCheck,
  Lock,
  HeartHandshake,
  Plus,
  Minus,
  CreditCard,
  Calendar,
  ReceiptEuro,
  MessageSquare,
  BookOpen,
  ScrollText,
  Users,
} from "lucide-react";

const PRICE = MONTHLY_PRICE_EUR;
const MIN_TOTAL = MONTHLY_MIN_TOTAL; // €32.97

// ---------- Reusable bits (mirror Landing / About tokens) -----------------

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">
    {children}
  </div>
);

const SectionHeading = ({ eyebrow, title, intro, center = false }) => (
  <div className={center ? "max-w-2xl mx-auto text-center" : "max-w-2xl"}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h2>
    {intro && (
      <p className="text-base leading-relaxed mt-4 text-[#48595E] dark:text-[#A8B6BB]">
        {intro}
      </p>
    )}
  </div>
);

const cardSurface =
  "border border-[#E5EBEC] dark:border-[#2A3539] " +
  "bg-white dark:bg-[#161E21] " +
  "rounded-xl shadow-[0_1px_2px_rgba(15,21,23,0.04)] " +
  "hover:shadow-[0_8px_24px_-12px_rgba(31,159,164,0.18)] " +
  "transition-shadow duration-200";

const FeatureCard = ({ icon: Icon, title, desc, testid }) => (
  <div className={`${cardSurface} p-6`} data-testid={testid}>
    <div className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
    </div>
    <h3 className="font-heading text-lg font-semibold mt-5 text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h3>
    <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">
      {desc}
    </p>
  </div>
);

const TrustBadge = ({ icon: Icon, label, testid }) => (
  <div
    className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white/70 dark:bg-[#161E21]/70 border border-[#D6F0EF] dark:border-[#1F9FA4]/30 backdrop-blur"
    data-testid={testid}
  >
    <Icon className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.8} />
    <span className="text-sm font-medium text-[#1A2225] dark:text-[#E8EEF0]">{label}</span>
  </div>
);

// ---------- FAQ ------------------------------------------------------------

const FAQ_ITEMS = [
  {
    q: "Is there a free trial?",
    a: "Yes — every new account starts with a 30-day free trial that gives you full access to every day-to-day feature: messaging, calendar, expenses, journal, documents and contact visits, with no card required and no storage limits. Exports, downloads, Court Bundle generation and external record sharing are paid-only capabilities from day one and unlock when you subscribe. If you decide to keep using CoParent Console after the 30 days, you add payment details and pick a plan (€10.99/month or €99/year). If you don't, your account moves to a 7-day read-only grace period — you can sign in, view every record, review Insights and browse Court Bundles — and is then locked until you subscribe. Your data is preserved indefinitely and nothing is deleted.",
  },
  {
    q: "What's the difference between monthly and yearly?",
    a: "Same product, same features — only the billing cadence changes. Monthly is €10.99/month with a 3-month minimum (after the free trial). Yearly is €99 charged once, up front, for a full year of access — that works out to €8.25 per month, saving you €32.88 versus paying monthly for the same period. There is no minimum on the yearly plan beyond the year you've paid for, and you can cancel any time (your access continues until the year you've paid for ends).",
  },
  {
    q: "What does each plan get me?",
    a: "Both plans include full read-and-write access to every feature: messaging, calendar and parenting schedules, expenses with splits and settlements, journal, document vault, child profiles, court-ready PDF exports, and the full audit trail. One subscription covers your parent account and every child linked to it — you're never charged separately per child.",
  },
  {
    q: "Why is there a 3-month minimum on the monthly plan?",
    a: "The 3-month minimum kicks in only AFTER your free trial ends and you decide to subscribe monthly. Co-parenting tools work best when you actually use them — the first weeks are the steepest learning curve. The 3-month minimum gives both you and your co-parent enough time to set up schedules, log a few expenses, and build a meaningful record. After that, you're on a rolling monthly plan and can cancel any time. The yearly plan has no monthly minimum because the upfront annual payment itself satisfies the commitment period.",
  },
  {
    q: "How does cancellation work?",
    a: "You can cancel during the free trial at any time, no questions, no charge. After you've subscribed, the rules differ slightly by plan. Monthly: a Cancel button unlocks in Settings → Subscription once you've made 3 successful monthly payments; cancellation takes effect at the end of your current billing cycle. Yearly: you can cancel at any time, and your access stays active until the end of the year you've already paid for. After cancellation, your account moves into a 7-day read-only grace period — sign in, view every record, review Insights and browse Court Bundles — and is then locked until you reactivate. Exports, downloads, Court Bundle generation and external shares stay paid-only throughout. No partial refunds are provided for unused time on either plan.",
  },
  {
    q: "Can I switch between monthly and yearly later?",
    a: "Yes. You can change your plan from Settings → Subscription. Switches take effect at your next renewal — we don't pro-rate mid-cycle, but you keep all access until the renewal date and the new plan starts cleanly from there.",
  },
  {
    q: "Do I need a card to sign up?",
    a: "No. Signup is just an email and a password. We only ask for payment details if and when you decide to subscribe at the end of the 30-day trial.",
  },
  {
    q: "Does my co-parent need their own subscription?",
    a: "Yes — billing is per parent account, not per household. Your co-parent has their own account and their own free trial, and their own subscription if they choose to keep it. They can pick their own monthly or yearly plan independently of yours. One of you can be on Paid and the other in the read-only grace or locked state; the read-only parent can still sign in and view every record, but can't add new entries, export or share externally until they subscribe.",
  },
  {
    q: "What happens after my trial ends if I don't subscribe?",
    a: "Your account moves to a 7-day read-only grace period: you can sign in, view every record, review Insights and browse Court Bundles and Documents. You can also subscribe (monthly or yearly) to reactivate full access at any time — but during grace you can't add new entries, send messages, log expenses, invite co-parents, export PDFs, download reports, generate a Court Bundle or share records externally. After those 7 days the account is locked. Sign-in still works and you'll see a clear 'Subscribe to regain access' prompt. Your data is preserved indefinitely — nothing is deleted.",
  },
  {
    q: "Do mediators or solicitors I invite need to pay?",
    a: "No. Professionals you invite get a free, view-only account scoped to whichever child you've shared with them. Their access depends on the inviting parent keeping their account active — if your account is removed, the professional's view is removed with it. They never see a paywall themselves.",
  },
  {
    q: "How do I pay? Is my card secure?",
    a: "Payments are handled by Stripe. Your card details are entered directly into Stripe's secure form and never touch our servers. We retain only billing metadata (last four digits, billing country, invoice history) — exactly what's needed to manage your subscription and produce receipts.",
  },
  {
    q: "Can I get an invoice or receipt?",
    a: "Yes. After every successful payment, Stripe emails you a receipt — that's once a month for monthly subscribers, once a year for yearly. You can also download all past invoices any time from Settings → Subscription. We send a separate branded confirmation email on your first payment summarising the price, term, renewal and cancellation policy for your chosen plan.",
  },
];

const FaqRow = ({ q, a, open, onToggle, testid }) => (
  <div
    className="border-b border-[#E5EBEC] dark:border-[#2A3539]"
    data-testid={testid}
  >
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 py-4 text-left hover:text-[#116B6E] dark:hover:text-[#3FB8BC] transition-colors min-h-[52px]"
      aria-expanded={open}
    >
      <span className="text-base font-medium text-[#1A2225] dark:text-[#F4F8F9]">
        {q}
      </span>
      {open
        ? <Minus className="w-4 h-4 text-[#48595E] dark:text-[#A8B6BB] flex-shrink-0" strokeWidth={1.7} />
        : <Plus className="w-4 h-4 text-[#48595E] dark:text-[#A8B6BB] flex-shrink-0" strokeWidth={1.7} />}
    </button>
    {open && (
      <div className="pb-5 pr-7 text-sm leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
        {a}
      </div>
    )}
  </div>
);

// ---------- Page -----------------------------------------------------------

export default function Pricing() {
  // Track open FAQ items by index. First item is open by default so
  // the "what does €10.99 get me?" answer is visible without a tap.
  const [open, setOpen] = useState({ 0: true });
  const toggle = (i) => setOpen((m) => ({ ...m, [i]: !m[i] }));

  return (
    <div
      className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#E8EEF0]"
      data-testid="pricing-page"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-30 border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] inline-flex items-center gap-1.5"
            data-testid="pricing-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Home</span>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="pricing-hero">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#E0F2F2]/60 via-white to-white dark:from-[#0F2F30]/70 dark:via-[#0E1416] dark:to-[#0E1416]"
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/15 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-12 text-center">
          <Eyebrow>Pricing</Eyebrow>
          <h1 className="font-heading text-[2rem] leading-[1.15] sm:text-5xl lg:text-[3.25rem] lg:leading-[1.05] font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            One plan. Everything included.
          </h1>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#48595E] dark:text-[#B6C2C7] max-w-2xl mx-auto">
            Sign up free with just an email — no card, no commitment. Try every
            feature for 30 days. After the trial, pick the cadence that works
            for you: <strong className="text-[#1A2225] dark:text-[#F4F8F9]">€10.99/month</strong> with a 3-month minimum,
            or <strong className="text-[#1A2225] dark:text-[#F4F8F9]">€99/year</strong> billed upfront and saving you €{YEARLY_SAVINGS_EUR}.
            No tiers, no upsells.
          </p>
        </div>
      </section>

      {/* PRICING CARDS — side-by-side Monthly + Yearly */}
      <section className="bg-white dark:bg-[#0E1416] pb-12 sm:pb-16" data-testid="pricing-card-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Free-trial banner above the cards. */}
          <div
            className="mb-8 rounded-xl border border-[#1F9FA4]/40 bg-gradient-to-br from-[#E0F2F2] to-white dark:from-[#0F2F30]/60 dark:to-[#161E21] p-5 sm:p-6 flex flex-col sm:flex-row gap-4 items-start"
            data-testid="pricing-trial-banner"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[#1F9FA4] text-white inline-flex items-center justify-center">
              <Sparkles className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0B4D50] dark:text-[#3FB8BC]">
                Try before you pay
              </div>
              <h3 className="font-heading text-xl sm:text-2xl font-semibold text-[#0B4D50] dark:text-[#F4F8F9] mt-1">
                30-day free trial. No card required.
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-[#0B4D50]/80 dark:text-[#A8B6BB]">
                Sign up with just an email and a password. You get full access
                to every day-to-day feature for 30 days — invite your co-parent,
                log a few expenses, send messages — and only enter payment
                details if you decide to keep using it. Exports, downloads,
                Court Bundle generation and external record sharing unlock
                when you subscribe.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5 lg:gap-6 items-stretch" data-testid="pricing-plans-grid">
            {/* MONTHLY CARD */}
            <div
              className="relative border border-[#D6F0EF] dark:border-[#2A3539] bg-white dark:bg-[#161E21] rounded-2xl p-6 sm:p-7 flex flex-col"
              data-testid="pricing-monthly-card"
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#48595E] dark:text-[#A8B6BB]">
                Monthly
              </div>
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="font-heading text-5xl font-semibold text-[#1A2225] dark:text-[#F4F8F9] tabular-nums" data-testid="pricing-monthly-amount">
                  €{PRICE.toFixed(2)}
                </span>
                <span className="text-base text-[#48595E] dark:text-[#A8B6BB]">/ month</span>
              </div>
              <p className="mt-3 text-sm text-[#48595E] dark:text-[#A8B6BB] leading-relaxed">
                Billed monthly after your free trial. Three-month minimum to get
                you and your co-parent properly set up; cancel any time after
                that.
              </p>
              <ul className="mt-5 inline-flex flex-col gap-1.5 text-xs">
                <li className="text-[#0B4D50] dark:text-[#3FB8BC]"><Check className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2.4} />30-day free trial</li>
                <li className="text-[#0B4D50] dark:text-[#3FB8BC]"><Check className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2.4} />3-month minimum, then rolling</li>
                <li className="text-[#0B4D50] dark:text-[#3FB8BC]"><Check className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2.4} />Total minimum charge €{MIN_TOTAL}</li>
              </ul>
              <div className="mt-auto pt-6">
                <Link
                  to="/auth?mode=register&utm_source=pricing&plan=monthly"
                  data-testid="pricing-monthly-cta"
                  onClick={() => trackEvent("cta_pricing_signup", { location: "pricing_monthly" })}
                >
                  <Button variant="outline" className="w-full h-11 px-6 text-base font-medium rounded-md border-[#116B6E] text-[#116B6E] dark:border-[#3FB8BC] dark:text-[#3FB8BC] hover:bg-[#E0F2F2] dark:hover:bg-[#1F9FA4]/15">
                    Choose monthly
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
                  </Button>
                </Link>
              </div>
            </div>

            {/* YEARLY CARD */}
            <div
              className="relative border-2 border-[#1F9FA4] bg-white dark:bg-[#161E21] rounded-2xl p-6 sm:p-7 flex flex-col shadow-[0_24px_48px_-24px_rgba(31,159,164,0.35)]"
              data-testid="pricing-yearly-card"
            >
              <div
                className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#116B6E] text-white text-[10px] uppercase tracking-[0.16em] font-semibold shadow-sm"
                data-testid="pricing-yearly-badge"
              >
                Best value · Save €{YEARLY_SAVINGS_EUR}
              </div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#116B6E] dark:text-[#3FB8BC]">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                Yearly
              </div>
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="font-heading text-5xl font-semibold text-[#1A2225] dark:text-[#F4F8F9] tabular-nums" data-testid="pricing-yearly-amount">
                  €{YEARLY_PRICE_EUR.toFixed(0)}
                </span>
                <span className="text-base text-[#48595E] dark:text-[#A8B6BB]">/ year</span>
              </div>
              <p className="mt-1 text-sm text-[#0B4D50] dark:text-[#3FB8BC] font-medium" data-testid="pricing-yearly-equiv">
                Equivalent to €{YEARLY_MONTHLY_EQUIV} / month
              </p>
              <p className="mt-3 text-sm text-[#48595E] dark:text-[#A8B6BB] leading-relaxed">
                Pay once for a full year of access. The annual payment IS the
                commitment, so you can cancel any time and keep access until
                the year you've paid for ends.
              </p>
              <ul className="mt-5 inline-flex flex-col gap-1.5 text-xs">
                <li className="text-[#0B4D50] dark:text-[#3FB8BC]"><Check className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2.4} />30-day free trial</li>
                <li className="text-[#0B4D50] dark:text-[#3FB8BC]"><Check className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2.4} />Cancel anytime, access stays until renewal</li>
                <li className="text-[#0B4D50] dark:text-[#3FB8BC]"><Check className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2.4} />Save €{YEARLY_SAVINGS_EUR} a year vs. monthly</li>
              </ul>
              <div className="mt-auto pt-6">
                <Link
                  to="/auth?mode=register&utm_source=pricing&plan=yearly"
                  data-testid="pricing-yearly-cta"
                  onClick={() => trackEvent("cta_pricing_signup", { location: "pricing_yearly" })}
                >
                  <Button className="w-full h-11 px-6 text-base font-medium rounded-md bg-[#116B6E] hover:bg-[#0F5658] text-white">
                    Choose yearly
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Shared what's-included list — applies to BOTH plans. */}
          <div className="mt-8 border border-[#E5EBEC] dark:border-[#2A3539] bg-[#FAFCFC] dark:bg-[#0B1011] rounded-xl p-6">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#48595E] dark:text-[#A8B6BB] mb-4">
              Both plans include
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#1A2225] dark:text-[#E8EEF0]">
              {[
                "Send and receive messages",
                "Log and split expenses",
                "Create and edit calendar events",
                "Create and respond to requests",
                "Upload shared documents",
                "Time-stamped journal entries",
                "Court-ready PDF exports",
                "Full immutable audit trail",
                "View-only invites for mediators",
                "All future features",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#1F9FA4] dark:text-[#3FB8BC] flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Tiny aside under the cards — payment processor + currency. */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-xs text-[#6F828A] dark:text-[#7A8A91]">
            <span className="inline-flex items-center gap-1.5" data-testid="pricing-stripe-note">
              <CreditCard className="w-3.5 h-3.5" strokeWidth={1.8} />
              Payments handled by Stripe
            </span>
            <span aria-hidden="true">·</span>
            <span>Prices in EUR</span>
            <span aria-hidden="true">·</span>
            <span>VAT included where applicable</span>
          </div>
        </div>
      </section>

      {/* AFTER-TRIAL STRIP — describes the new 7+7+lock model. */}
      <section className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]" data-testid="pricing-after-trial">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
          <div className="grid sm:grid-cols-12 gap-6 items-start">
            <div className="sm:col-span-4">
              <Eyebrow>After your trial</Eyebrow>
              <h3 className="font-heading text-2xl font-semibold mt-2 text-[#1A2225] dark:text-[#F4F8F9]">
                A clear, fair runway
              </h3>
            </div>
            <div className="sm:col-span-8 text-sm sm:text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7] space-y-3">
              <p>
                If you don&apos;t subscribe at the end of the 30-day free trial,
                your account moves to a <strong className="text-[#1A2225] dark:text-[#F4F8F9]">7-day read-only grace period</strong>.
                You can sign in, view every record you&apos;ve created, review
                Insights and browse your Court Bundles and Documents — but you
                can&apos;t add new entries, send messages, invite anyone new,
                export PDFs, download reports, generate a Court Bundle or share
                records externally.
              </p>
              <p>
                After those 7 grace days, the account is <strong className="text-[#1A2225] dark:text-[#F4F8F9]">locked</strong> until
                you subscribe. Sign-in still works and shows a clear
                &ldquo;Subscribe to regain access&rdquo; prompt with a support
                contact link. <strong className="text-[#1A2225] dark:text-[#F4F8F9]">Your data is preserved indefinitely — nothing is deleted</strong> —
                so you can resume where you left off whenever you subscribe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED — feature grid */}
      <section className="bg-white dark:bg-[#0E1416]" data-testid="pricing-features">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Everything included"
            title="One subscription. The full platform."
            intro="No tiered features locked behind 'Premium'. Every paid account gets the same complete set of tools."
            center
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-10">
            <FeatureCard icon={Calendar} title="Shared calendar" desc="Schedules, events, handovers and reminders." testid="pricing-feat-calendar" />
            <FeatureCard icon={ReceiptEuro} title="Expenses &amp; settlements" desc="Splits, receipts and clear ledgers." testid="pricing-feat-expenses" />
            <FeatureCard icon={MessageSquare} title="Immutable messaging" desc="Time-stamped, tamper-evident records." testid="pricing-feat-messaging" />
            <FeatureCard icon={BookOpen} title="Journal &amp; documents" desc="Capture incidents, keep records." testid="pricing-feat-journal" />
            <FeatureCard icon={ScrollText} title="Court-ready PDF exports" desc="Bundle records for mediation or court." testid="pricing-feat-exports" />
            <FeatureCard icon={Users} title="View-only professional invites" desc="Mediators and solicitors at no extra cost." testid="pricing-feat-pro-invites" />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]" data-testid="pricing-trust">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4">
            <TrustBadge icon={HeartHandshake} label="Proudly Irish Built" testid="pricing-trust-irish" />
            <TrustBadge icon={Lock} label="Privacy-first" testid="pricing-trust-privacy" />
            <TrustBadge icon={ShieldCheck} label="Court-ready records" testid="pricing-trust-court" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white dark:bg-[#0E1416]" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Common questions"
            title="Pricing &amp; billing, in plain English"
          />
          <div className="mt-10">
            {FAQ_ITEMS.map((it, i) => (
              <FaqRow
                key={it.q}
                q={it.q}
                a={it.a}
                open={!!open[i]}
                onToggle={() => toggle(i)}
                testid={`pricing-faq-${i}`}
              />
            ))}
          </div>
          <p className="mt-8 text-sm text-[#48595E] dark:text-[#A8B6BB]">
            Still have a question? <Link to="/contact" className="text-[#116B6E] dark:text-[#3FB8BC] underline-offset-2 hover:underline">Get in touch</Link> — we read every email.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-gradient-to-b from-[#F4FAFA] to-white dark:from-[#0F2F30]/30 dark:to-[#0E1416] border-y border-[#E5EBEC] dark:border-[#1F2A2D] overflow-hidden" data-testid="pricing-final-cta">
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/15 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1] text-[#1A2225] dark:text-[#F4F8F9]">
            Ready to bring some calm to co-parenting?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#48595E] dark:text-[#A8B6BB] max-w-xl mx-auto">
            30 days free, no card required. Then €{PRICE.toFixed(2)}/month with a 3-month minimum, or €{YEARLY_PRICE_EUR.toFixed(0)}/year — saving €{YEARLY_SAVINGS_EUR}.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
            <Link to="/auth?mode=register&utm_source=pricing_final" data-testid="pricing-final-create" className="w-full sm:w-auto" onClick={() => trackEvent("cta_pricing_signup", { location: "pricing_final" })}>
              <Button className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-12 px-8 text-base font-medium shadow-sm transition-colors">
                Start free trial
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Button>
            </Link>
            <Link to="/contact" data-testid="pricing-final-contact" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto h-12 px-8 text-base rounded-md border-[#D6F0EF] dark:border-[#2A3539] bg-white dark:bg-[#161E21]">
                Contact us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1011]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-heading font-semibold text-sm text-[#1A2225] dark:text-[#F4F8F9]">CoParent Console</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#6F828A] dark:text-[#7A8A91] flex-wrap justify-center">
            <Link to="/about" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">About</Link>
            <span aria-hidden="true">·</span>
            <Link to="/professionals" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">For professionals</Link>
            <span aria-hidden="true">·</span>
            <Link to="/help" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Help</Link>
            <span aria-hidden="true">·</span>
            <Link to="/contact" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Contact</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/security" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Security</Link>
          </div>
        </div>
        <LegalFooterInfo />
      </footer>
    </div>
  );
}
